import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const RecirculationCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(5);

  // Recirculation categories data
  const categories = [
    {
      title: "Hotels & Resorts",
      image: "/images/journey/1_journey_lifetime.jpeg", // Using placeholder - would use specific images
      description: "Discover luxury hotels and resorts worldwide"
    },
    {
      title: "Ritz-Carlton Reserve",
      image: "/images/journey/2_journey_lifetime.jpeg",
      description: "Rare escapes in the world's most pristine locations"
    },
    {
      title: "Yachts",
      image: "/images/journey/3_journey_lifetime.jpeg",
      description: "Intimate luxury yacht experiences"
    },
    {
      title: "Safari",
      image: "/images/journey/4_journey_lifetime.jpeg",
      description: "African safari adventures in luxury"
    },
    {
      title: "Residences",
      image: "/images/journey/1_journey_lifetime.jpeg",
      description: "Luxury residential living experiences"
    }
  ];

  useEffect(() => {
    const updateItemsPerView = () => {
      if (window.innerWidth >= 1280) setItemsPerView(5);
      else if (window.innerWidth >= 1024) setItemsPerView(4);
      else if (window.innerWidth >= 768) setItemsPerView(3);
      else if (window.innerWidth >= 640) setItemsPerView(2);
      else setItemsPerView(1);
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % Math.max(1, categories.length - itemsPerView + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + Math.max(1, categories.length - itemsPerView + 1)) % Math.max(1, categories.length - itemsPerView + 1));
  };

  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < categories.length - itemsPerView;

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        
        {/* Carousel Container */}
        <div className="relative">
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ 
                transform: `translateX(-${(currentIndex * 100) / itemsPerView}%)`,
              }}
            >
              {categories.map((category, index) => (
                <div 
                  key={index} 
                  className="flex-shrink-0 px-3"
                  style={{ width: `${100 / itemsPerView}%` }}
                >
                  <div className="group cursor-pointer">
                    {/* Image */}
                    <div className="relative overflow-hidden mb-4">
                      <img
                        src={category.image}
                        alt={category.title}
                        className="w-full h-[300px] object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    
                    {/* Content */}
                    <div className="text-center space-y-2">
                      <h3 
                        className="text-gray-900 font-medium"
                        style={{
                          fontFamily: 'Swiss-721, Arial, sans-serif',
                          fontSize: '1.125rem',
                          fontWeight: '500'
                        }}
                      >
                        {category.title}
                      </h3>
                      
                      {/* Separator Line */}
                      <div className="w-full h-px bg-gray-300 mx-auto"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows - Only show if needed */}
          {categories.length > itemsPerView && (
            <>
              <button 
                onClick={prevSlide}
                disabled={!canGoPrev}
                className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white shadow-lg p-2 rounded-full transition-all duration-200 z-10 ${
                  canGoPrev 
                    ? 'text-gray-800 hover:bg-gray-50' 
                    : 'text-gray-400 cursor-not-allowed'
                }`}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              <button 
                onClick={nextSlide}
                disabled={!canGoNext}
                className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white shadow-lg p-2 rounded-full transition-all duration-200 z-10 ${
                  canGoNext 
                    ? 'text-gray-800 hover:bg-gray-50' 
                    : 'text-gray-400 cursor-not-allowed'
                }`}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}

          {/* Dots Indicator - Only show if needed */}
          {categories.length > itemsPerView && (
            <div className="flex justify-center mt-8 space-x-2">
              {Array.from({ length: Math.max(1, categories.length - itemsPerView + 1) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                    currentIndex === index ? 'bg-gray-800' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default RecirculationCarousel;

